// 翻页音效合成器：基于 Web Audio API 实时生成纸感翻页声
// 通过噪声脉冲 + 滤波整形模拟真实纸张摩擦与翻动

type SoundKind = "crisp" | "soft" | "old" | "pop" | "tap" | "rapid";

interface SoundProfile {
  filterFrom: number;
  filterTo: number;
  gain: number;
  duration: number;
  tone: number;
}

const profiles: Record<SoundKind, SoundProfile> = {
  crisp: { filterFrom: 900, filterTo: 2600, gain: 0.5, duration: 0.42, tone: 180 },
  soft: { filterFrom: 500, filterTo: 1600, gain: 0.34, duration: 0.62, tone: 120 },
  old: { filterFrom: 380, filterTo: 1300, gain: 0.4, duration: 0.55, tone: 90 },
  pop: { filterFrom: 700, filterTo: 2400, gain: 0.46, duration: 0.34, tone: 300 },
  tap: { filterFrom: 1200, filterTo: 3200, gain: 0.3, duration: 0.16, tone: 420 },
  rapid: { filterFrom: 600, filterTo: 2200, gain: 0.38, duration: 1.0, tone: 160 },
};

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") {
    void ctx.resume();
  }
  return ctx;
}

function buildNoise(audioCtx: AudioContext, duration: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.floor(audioCtx.sampleRate * duration));
  const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buffer;
  return src;
}

export function playFlipSound(kind: SoundKind, volume = 1): void {
  const audioCtx = getCtx();
  if (!audioCtx) return;

  const p = profiles[kind];
  const now = audioCtx.currentTime;

  // 噪声层：纸张摩擦
  const noise = buildNoise(audioCtx, p.duration);
  const bandpass = audioCtx.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.Q.value = 0.9;
  bandpass.frequency.setValueAtTime(p.filterFrom, now);
  bandpass.frequency.exponentialRampToValueAtTime(p.filterTo, now + p.duration);

  const noiseGain = audioCtx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, now);
  noiseGain.gain.exponentialRampToValueAtTime(p.gain * volume, now + 0.02);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + p.duration);

  noise.connect(bandpass);
  bandpass.connect(noiseGain);
  noiseGain.connect(audioCtx.destination);
  noise.start(now);
  noise.stop(now + p.duration + 0.05);

  // 音调层：翻页瞬间的轻微冲击
  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(p.tone, now);
  osc.frequency.exponentialRampToValueAtTime(p.tone * 1.6, now + 0.12);
  const oscGain = audioCtx.createGain();
  oscGain.gain.setValueAtTime(0.0001, now);
  oscGain.gain.exponentialRampToValueAtTime(0.12 * volume, now + 0.02);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  osc.connect(oscGain);
  oscGain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}